import React, { useEffect, useState } from "react";
import PublicLayout from "../components/PublicLayout";
import { api, formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Upload, Send, Image as ImageIcon, Package, Check, Trash2, FileSpreadsheet, Settings as SettingsIcon, CalendarClock, Clock, CalendarDays } from "lucide-react";
import { ProgressBar } from "../components/ProgressBar";

export default function SchoolDashboard() {
  const { user, loading } = useAuth();
  const [profile, setProfile] = useState(null);
  const [students, setStudents] = useState([]);
  const [uploadBatches, setUploadBatches] = useState([]);
  const [batches, setBatches] = useState([]);
  const [donations, setDonations] = useState([]);
  const [proofs, setProofs] = useState([]);
  const [visits, setVisits] = useState([]);
  const [tab, setTab] = useState("overview");

  const load = async () => {
    const [p, s, ub, b, d, pr, vs] = await Promise.all([
      api.get("/school/profile").then(r=>r.data).catch(()=>null),
      api.get("/school/students").then(r=>r.data).catch(()=>[]),
      api.get("/school/student-batches").then(r=>r.data).catch(()=>[]),
      api.get("/school/batches").then(r=>r.data).catch(()=>[]),
      api.get("/school/donations").then(r=>r.data).catch(()=>[]),
      api.get("/school/proofs").then(r=>r.data).catch(()=>[]),
      api.get("/school/schedules").then(r=>r.data).catch(()=>[]),
    ]);
    setProfile(p); setStudents(s); setUploadBatches(ub); setBatches(b); setDonations(d); setProofs(pr); setVisits(vs);
  };

  useEffect(() => { if (user) load(); }, [user]);

  if (loading) return <PublicLayout><div className="p-20 text-center">Loading…</div></PublicLayout>;
  if (!user || user.role !== "school") return <PublicLayout><div className="p-20 text-center">Unauthorized. <a className="bb-link" href="/login">Sign in as school</a>.</div></PublicLayout>;

  const handleExcel = async (files) => {
    const fileList = Array.isArray(files) ? files : [files];
    let totalAdded = 0;
    let cumulativeTotal = 0;
    let failures = 0;
    for (const file of fileList) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/school/students/upload-excel", fd, { headers: { "Content-Type": "multipart/form-data" }});
        totalAdded += data.added || 0;
        cumulativeTotal = data.total_students;
      } catch (e) {
        failures += 1;
        toast.error(`${file.name}: ${formatError(e)}`);
      }
    }
    if (totalAdded > 0) {
      toast.success(`Added ${totalAdded} students from ${fileList.length - failures} file${fileList.length - failures === 1 ? "" : "s"} · total ${cumulativeTotal}`);
    }
    load();
  };

  const clearStudents = async () => {
    if (!confirm("Remove ALL students and start over? This is only allowed before approval.")) return;
    try { await api.delete("/school/students"); toast.success("Student list cleared."); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const submitRequest = async () => {
    try { await api.post("/school/request/submit"); toast.success("Request submitted."); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const confirmDelivery = async (batchId) => {
    const qty = prompt("Quantity delivered?", "0");
    if (!qty) return;
    try { await api.post(`/school/delivery/${batchId}/confirm`, { delivered_quantity: parseInt(qty, 10) }); toast.success("Delivery confirmed."); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const uploadProof = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("caption", "");
    try { await api.post("/school/proof/upload", fd, { headers: { "Content-Type": "multipart/form-data" }}); toast.success("Proof uploaded. Awaiting admin review."); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const uploadLogo = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try { await api.post("/school/logo", fd, { headers: { "Content-Type": "multipart/form-data" }}); toast.success("Logo updated."); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const saveProfile = async (form) => {
    try {
      await api.put("/school/profile", form);
      toast.success("Profile saved.");
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const locked = profile?.status === "completed";

  return (
    <PublicLayout>
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="bb-overline text-[var(--bb-terracotta)] mb-3">SCHOOL DASHBOARD</div>
        <div className="flex items-start gap-5">
          <label className="relative shrink-0 cursor-pointer group" data-testid="school-logo-upload">
            <div className="w-20 h-20 rounded-full overflow-hidden border border-[var(--bb-border)] bg-[var(--bb-sand)] grid place-items-center">
              {profile?.logo_path ? (
                <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${profile.logo_path}`} alt="" className="w-full h-full object-cover"/>
              ) : (
                <span className="font-serif text-3xl text-[var(--bb-terracotta)]">{profile?.name?.[0] || "?"}</span>
              )}
            </div>
            <div className="absolute inset-0 rounded-full bg-black/40 text-white text-[10px] uppercase tracking-wider grid place-items-center opacity-0 group-hover:opacity-100 transition">Upload</div>
            <input type="file" accept="image/*" className="hidden" onChange={(e)=>e.target.files[0] && uploadLogo(e.target.files[0])}/>
          </label>
          <div>
            <h1 className="font-serif text-4xl md:text-5xl text-[var(--bb-forest)] tracking-tight">{profile?.name}</h1>
            <div className="text-sm text-[var(--bb-muted)] mt-1">{profile?.address}</div>
          </div>
        </div>

        <div className="mt-8 grid sm:grid-cols-4 gap-4">
          <Tile label="Status" value={profile?.status} />
          <Tile label="Students" value={profile?.total_students || 0} />
          <Tile label="Books required" value={profile?.total_books_required || 0} />
          <Tile label="Books funded" value={profile?.total_books_funded || 0} />
        </div>

        <div className="mt-6 bb-card p-5">
          <ProgressBar value={profile?.total_books_funded || 0} total={profile?.total_books_required || 1} />
        </div>

        <div className="mt-10 flex flex-wrap gap-2 border-b border-[var(--bb-border)]">
          {[["overview","ಅವಲೋಕನ"],["students","ವಿದ್ಯಾರ್ಥಿಗಳು"],["batches","ಬ್ಯಾಚ್ ಮತ್ತು ವಿತರಣೆ"],["proofs","ಪುರಾವೆ ಅಪ್ಲೋಡ್"],["donations","ದೇಣಿಗೆಗಳು"],["visits","ಮುಂಬರುವ ಭೇಟಿಗಳು"],["settings","ಸೆಟ್ಟಿಂಗ್ಸ್"]].map(([k,l])=> (
            <button key={k} onClick={()=>setTab(k)} className={`px-5 py-3 text-sm ${tab===k?"border-b-2 border-[var(--bb-terracotta)] text-[var(--bb-terracotta)]":"text-[var(--bb-muted)]"}`} data-testid={`school-tab-${k}`}>
              {l}
              {k === "visits" && visits.length > 0 && ` (${visits.length})`}
            </button>
          ))}
        </div>

        <div className="mt-8">
          {tab === "overview" && (
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bb-card p-8">
                <div className="bb-overline text-[var(--bb-muted)] mb-2">CURRENT STATUS</div>
                <div className="font-serif text-3xl text-[var(--bb-forest)] mb-4">{profile?.status}</div>
                <p className="text-sm text-[var(--bb-ink)]/75 mb-6">
                  {profile?.status === "pending" && "Admin is reviewing your student list. Once approved, donations open up."}
                  {profile?.status === "approved" && "Approved! Your school is now visible to donors."}
                  {profile?.status === "active" && "Active. Donations are flowing in."}
                  {profile?.status === "rejected" && "Rejected. Please contact admin."}
                </p>
                {!locked && students.length > 0 && profile?.status === "pending" && (
                  <button onClick={submitRequest} className="bb-btn-primary inline-flex items-center gap-2" data-testid="submit-request"><Send size={14}/> Submit for review</button>
                )}
              </div>
              <div className="bb-card p-8">
                <div className="bb-overline text-[var(--bb-muted)] mb-2">NEXT STEP</div>
                <div className="font-serif text-2xl text-[var(--bb-forest)] mb-4">
                  {students.length === 0 ? "Upload your first student list" : (locked ? "Wait for admin actions" : `${students.length} students added — submit or add more`)}
                </div>
                {!locked && (
                  <label className="bb-btn-secondary inline-flex items-center gap-2 cursor-pointer">
                    <Upload size={14}/> {students.length === 0 ? "Upload Excel file(s)" : "Add more students (Excel)"}
                    <input type="file" accept=".xlsx,.xls" multiple className="hidden" onChange={(e)=>e.target.files.length && handleExcel(Array.from(e.target.files))} data-testid="upload-excel"/>
                  </label>
                )}
              </div>
            </div>
          )}

          {tab === "students" && (
            <div className="space-y-6">
              <div className="bb-card p-6">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <div>
                    <div className="font-serif text-2xl text-[var(--bb-forest)]">{students.length} students · {students.length} books required</div>
                    <div className="text-xs text-[var(--bb-muted)] mt-1">Upload one or more Excel files — students are <b>added</b> to the list (first come, first served). Student data stays PRIVATE.</div>
                  </div>
                  {!locked && (
                    <div className="flex items-center gap-2">
                      <label className="inline-flex items-center gap-2 bb-btn-primary cursor-pointer" data-testid="students-upload-btn">
                        <Upload size={14}/> Add students (Excel)
                        <input type="file" accept=".xlsx,.xls" multiple className="hidden" onChange={(e)=>e.target.files.length && handleExcel(Array.from(e.target.files))}/>
                      </label>
                      {students.length > 0 && (
                        <button onClick={clearStudents} className="text-xs text-[var(--bb-error)] hover:underline inline-flex items-center gap-1" data-testid="clear-students"><Trash2 size={12}/> Clear all</button>
                      )}
                    </div>
                  )}
                </div>
                <div className="text-xs text-[var(--bb-muted)] mb-2">Excel columns expected: <code>student_name</code>, <code>class</code>. Header row required.</div>

                {uploadBatches.length > 0 && (
                  <div className="mb-4 border border-[var(--bb-border)] rounded-md p-3 bg-[var(--bb-sand)]/60">
                    <div className="bb-overline text-[var(--bb-muted)] mb-2">UPLOAD HISTORY</div>
                    <ul className="text-xs space-y-1.5">
                      {uploadBatches.map(b => (
                        <li key={b.batch_id} className="flex items-center justify-between" data-testid={`upload-batch-${b.batch_id}`}>
                          <span className="flex items-center gap-2"><FileSpreadsheet size={12} className="text-[var(--bb-forest)]"/> {b.filename || (b.source === "excel" ? "Excel upload" : "Manual entry")}</span>
                          <span className="text-[var(--bb-muted)]">+{b.added_count} · {b.created_at?.slice(0, 16).replace("T", " ")}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="max-h-96 overflow-auto border border-[var(--bb-border)] rounded-md">
                  <table className="w-full text-sm">
                    <thead className="bb-overline text-[var(--bb-muted)] bg-[var(--bb-sand)] sticky top-0">
                      <tr>
                        <th className="text-left p-2 w-14">#</th>
                        <th className="text-left p-2">Name</th>
                        <th className="text-left p-2">Class</th>
                        <th className="text-left p-2">Added</th>
                      </tr>
                    </thead>
                    <tbody>
                      {students.map(s => (
                        <tr key={s.upload_id} className="border-t border-[var(--bb-border)]">
                          <td className="p-2 text-[var(--bb-muted)] font-mono">{s.sequence}</td>
                          <td className="p-2">{s.student_name}</td>
                          <td className="p-2">{s.student_class}</td>
                          <td className="p-2 text-xs text-[var(--bb-muted)]">{s.created_at?.slice(0, 10)}</td>
                        </tr>
                      ))}
                      {students.length === 0 && (
                        <tr><td colSpan="4" className="p-8 text-center text-sm text-[var(--bb-muted)]">No students yet. Upload an Excel to get started.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {tab === "batches" && (
            <div className="bb-card p-6">
              {batches.length === 0 ? <div className="p-6 text-sm text-[var(--bb-muted)] text-center">No batches yet. Admin will create one when donations are sufficient.</div> : (
                <table className="w-full text-sm">
                  <thead className="bb-overline text-[var(--bb-muted)]"><tr><th className="text-left p-2">Batch</th><th className="text-left p-2">Books</th><th className="text-left p-2">Tracking</th><th className="text-left p-2">Dispatch</th><th className="p-2"></th></tr></thead>
                  <tbody>
                    {batches.map(b=>(
                      <tr key={b.batch_id} className="border-t border-[var(--bb-border)]">
                        <td className="p-2 font-mono text-xs">{b.batch_id}</td>
                        <td className="p-2">{b.total_books}</td>
                        <td className="p-2 font-mono text-xs">{b.tracking_id || "—"}</td>
                        <td className="p-2"><span className="bb-tag">{b.dispatch_status}</span></td>
                        <td className="p-2 text-right">{b.dispatch_status === "dispatched" && <button onClick={()=>confirmDelivery(b.batch_id)} className="bb-btn-secondary !py-1.5 !px-3 text-xs" data-testid={`confirm-${b.batch_id}`}><Check size={12} className="inline"/> Confirm delivery</button>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {tab === "proofs" && (
            <div>
              <label className="inline-flex items-center gap-2 bb-btn-primary cursor-pointer mb-6" data-testid="proof-upload-btn">
                <ImageIcon size={14}/> Upload photo/video
                <input type="file" accept="image/*,video/*" className="hidden" onChange={(e)=>e.target.files[0] && uploadProof(e.target.files[0])}/>
              </label>
              <div className="grid sm:grid-cols-3 gap-4">
                {proofs.map(p=>(
                  <div key={p.proof_id} className="bb-card p-4">
                    <div className="aspect-video bg-[var(--bb-sand)] rounded overflow-hidden mb-3 grid place-items-center text-[var(--bb-muted)]">
                      {p.type==="image" ? <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p.storage_path}`} alt="" className="w-full h-full object-cover"/> : <Package size={28}/>}
                    </div>
                    <div className="text-xs text-[var(--bb-muted)]">{p.original_filename}</div>
                    <div className="mt-2"><span className="bb-tag">{p.approval_status}</span></div>
                  </div>
                ))}
                {proofs.length === 0 && <div className="col-span-full bb-card p-8 text-center text-sm text-[var(--bb-muted)]">No proofs yet.</div>}
              </div>
            </div>
          )}

          {tab === "donations" && (
            <div className="bb-card overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[var(--bb-sand)] bb-overline text-[var(--bb-muted)]"><tr><th className="p-3 text-left">Date</th><th className="p-3 text-left">Donor</th><th className="p-3 text-right">Sets</th></tr></thead>
                <tbody>
                  {donations.map(d=>(<tr key={d.donation_id} className="border-t border-[var(--bb-border)]"><td className="p-3">{d.donation_date?.slice(0,10)}</td><td className="p-3">{d.anonymous?"Anonymous":(d.donor_name_public||d.donor_name)}</td><td className="p-3 text-right">{d.quantity}</td></tr>))}
                  {donations.length===0 && <tr><td colSpan="3" className="p-8 text-center text-[var(--bb-muted)] text-sm">No donations yet.</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          {tab === "settings" && profile && (
            <SettingsPanel profile={profile} onUploadLogo={uploadLogo} onSave={saveProfile} />
          )}

          {tab === "visits" && (
            <div data-testid="school-visits-tab">
              <h3 className="font-serif text-2xl text-[var(--bb-forest)] mb-2">ಮುಂಬರುವ ದಾನಿಗಳ ಭೇಟಿಗಳು</h3>
              <p className="text-xs text-[var(--bb-muted)] mb-6">ದಾನಿಗಳು ಖುದ್ದಾಗಿ ಪುಸ್ತಕ ವಿತರಣೆಯಲ್ಲಿ ಭಾಗವಹಿಸಲು ನಿಗದಿಪಡಿಸಿದ ಭೇಟಿಗಳು ಇಲ್ಲಿವೆ.</p>
              {visits.length === 0 ? (
                <div className="bb-card p-10 text-center text-sm text-[var(--bb-muted)]">ಯಾವುದೇ ನಿಗದಿತ ಭೇಟಿಗಳಿಲ್ಲ.</div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {visits.map(v => (
                    <div key={v.schedule_id} className="bb-card p-5" data-testid={`school-visit-${v.schedule_id}`}>
                      <div className="flex items-center justify-between mb-3">
                        <span className={`bb-tag inline-flex items-center gap-1 ${v.status === "rescheduled" ? "" : "bb-tag-green"}`}>
                          <CalendarClock size={11}/> {v.status === "rescheduled" ? "ಪುನಃ ನಿಗದಿತ" : "ನಿಗದಿತ"}
                        </span>
                      </div>
                      <div className="font-serif text-lg text-[var(--bb-forest)] mb-3">{v.donor_name || "ದಾನಿ"}</div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="flex items-center gap-2"><CalendarDays size={13} className="text-[var(--bb-muted)]"/> {v.scheduled_date}</div>
                        <div className="flex items-center gap-2"><Clock size={13} className="text-[var(--bb-muted)]"/> {v.scheduled_time}</div>
                      </div>
                      {v.notes && <div className="mt-3 text-xs text-[var(--bb-muted)] italic line-clamp-2">"{v.notes}"</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}

function Tile({ label, value }) {
  return (
    <div className="bb-card p-5">
      <div className="bb-overline text-[var(--bb-muted)]">{label}</div>
      <div className="font-serif text-3xl text-[var(--bb-forest)] mt-1">{value}</div>
    </div>
  );
}

function SettingsPanel({ profile, onUploadLogo, onSave }) {
  const [form, setForm] = React.useState({
    school_name: profile.name || "",
    contact_person: profile.contact_person || "",
    phone: profile.phone || "",
    address: profile.address || "",
  });
  const fileRef = React.useRef(null);
  const [dragging, setDragging] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  const logoUrl = profile.logo_path
    ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${profile.logo_path}`
    : null;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) onUploadLogo(f);
  };

  return (
    <div className="grid md:grid-cols-5 gap-6">
      <div className="md:col-span-2 bb-card p-8" data-testid="settings-logo-card">
        <div className="bb-overline text-[var(--bb-muted)] mb-2 flex items-center gap-2"><ImageIcon size={14}/> SCHOOL LOGO</div>
        <p className="text-xs text-[var(--bb-muted)] mb-6">Shown to donors on every card and your detail page. Square images (512×512+) work best. JPG, PNG or WebP, under 2MB.</p>

        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          onClick={() => fileRef.current?.click()}
          className={`relative border-2 border-dashed rounded-md p-6 text-center cursor-pointer transition ${dragging ? "border-[var(--bb-terracotta)] bg-[var(--bb-terracotta)]/5" : "border-[var(--bb-border)] hover:border-[var(--bb-terracotta)]"}`}
          data-testid="logo-dropzone"
        >
          <div className="w-28 h-28 rounded-full mx-auto overflow-hidden border border-[var(--bb-border)] bg-[var(--bb-sand)] grid place-items-center mb-4">
            {logoUrl ? (
              <img src={logoUrl} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="font-serif text-5xl text-[var(--bb-terracotta)]">{profile.name?.[0] || "?"}</span>
            )}
          </div>
          <div className="text-sm text-[var(--bb-ink)]">
            <b className="text-[var(--bb-terracotta)]">Click to choose</b> or drop an image here
          </div>
          <div className="text-xs text-[var(--bb-muted)] mt-1">JPG · PNG · WebP</div>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={(e) => e.target.files?.[0] && onUploadLogo(e.target.files[0])}
            data-testid="settings-logo-input"
          />
        </div>

        {logoUrl && (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="mt-4 text-xs text-[var(--bb-forest)] hover:text-[var(--bb-terracotta)] inline-flex items-center gap-1"
            data-testid="settings-logo-replace"
          >
            <Upload size={12} /> Replace logo
          </button>
        )}
      </div>

      <form onSubmit={submit} className="md:col-span-3 bb-card p-8 space-y-5" data-testid="settings-profile-form">
        <div className="bb-overline text-[var(--bb-muted)] flex items-center gap-2"><SettingsIcon size={14}/> SCHOOL PROFILE</div>
        <p className="text-xs text-[var(--bb-muted)] -mt-3">These details appear on your public school page. Update whenever the contact person or address changes.</p>

        <Field label="School name">
          <input required value={form.school_name} onChange={(e)=>setForm({...form, school_name: e.target.value})} className="settings-field" data-testid="settings-school-name"/>
        </Field>
        <Field label="Contact person">
          <input required value={form.contact_person} onChange={(e)=>setForm({...form, contact_person: e.target.value})} className="settings-field" data-testid="settings-contact-person"/>
        </Field>
        <Field label="Phone">
          <input required value={form.phone} onChange={(e)=>setForm({...form, phone: e.target.value})} className="settings-field" data-testid="settings-phone"/>
        </Field>
        <Field label="Address">
          <textarea required rows={2} value={form.address} onChange={(e)=>setForm({...form, address: e.target.value})} className="settings-field resize-none" data-testid="settings-address"/>
        </Field>

        <style>{`.settings-field{width:100%;background:transparent;border-bottom:1px solid var(--bb-border);padding:8px 0;outline:none;font-size:15px;transition:border-color .2s} .settings-field:focus{border-color:var(--bb-terracotta)}`}</style>

        <div className="flex items-center gap-4 pt-2">
          <button type="submit" disabled={saving} className="bb-btn-primary" data-testid="settings-save">
            {saving ? "Saving…" : "Save profile"}
          </button>
          <span className="text-xs text-[var(--bb-muted)]">Changes apply immediately to donor-facing pages.</span>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="bb-overline text-[var(--bb-muted)]">{label}</label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
